// How usable a given image is as a link-preview card.
//
// Shared because two sides must agree: the storefront decides which image to
// emit as `og:image`, and the CMS meta-info editor warns while the image is
// still being chosen. A ceiling that drifts between them would let the editor
// bless an image the crawler then drops.

/**
 * WhatsApp silently drops a preview image over ~600 KB — the link then renders
 * with no picture at all, which is how a product hero straight off uploadthing
 * (routinely 800 KB+) ends up showing nothing. Other platforms are far more
 * generous (Facebook allows 8 MB), so this is the tightest common ceiling, not
 * a hard limit: `heavy` is a reason to prefer a lighter image, not to withhold.
 */
export const MAX_SHARE_IMAGE_BYTES = 600_000;

const PROBE_TIMEOUT_MS = 2_000;
const CACHE_TTL_MS = 600_000;

/**
 * `ok` — every platform can render it.
 * `heavy` — decodable, but past the tightest platform's ceiling. Worth
 *   replacing when there is something to replace it with; still better than no
 *   image at all on the platforms that would have shown it.
 * `unusable` — no platform can render it (SVG) or nobody can fetch it. Never
 *   emitted: a tag pointing at a broken image is worse than no tag.
 */
export type ShareImageVerdict = 'ok' | 'heavy' | 'unusable';

/** What one probe learned. `bytes` is null when the server would not say. */
export type ShareImageProbe = {
  verdict: ShareImageVerdict;
  bytes: number | null;
};

// A shared product is fetched again on every re-share, so remember the result
// per URL. Module-level: one map per process, keys are public URLs only.
const probes = new Map<string, ShareImageProbe & { at: number }>();

// `content-range: bytes 0-0/811940` — the total after the slash.
const totalFromContentRange = (header: string | null): number => {
  const total = header?.split('/')[1];
  return total ? Number(total) : Number.NaN;
};

/** The verdict for an already-fetched response. Pure, so it tests without a network. */
export const shareImageVerdictFromHeaders = (input: {
  ok: boolean;
  contentType: string;
  size: number;
}): ShareImageVerdict => {
  const { ok, contentType, size } = input;
  if (
    !ok ||
    contentType.includes('svg') ||
    (contentType && !contentType.startsWith('image/'))
  ) {
    return 'unusable';
  }
  return Number.isFinite(size) && size > MAX_SHARE_IMAGE_BYTES ? 'heavy' : 'ok';
};

/**
 * An SVG spotted from the URL alone, before any request.
 *
 * The probe already rejects `image/svg+xml`, but it fails open on timeout, and
 * the editor wants to say so the moment a URL is pasted. Extension only — a
 * `.svg` served as something else is still caught by the probe.
 */
export const looksLikeSvgUrl = (url: string): boolean =>
  /\.svg(\?|#|$)/i.test(url.trim());

/**
 * How usable is this URL as a preview card?
 *
 * Asks for a single byte rather than issuing a HEAD: Node's `fetch` does not
 * expose `content-length` on a HEAD to uploadthing (curl does — undici is
 * stricter), so a HEAD-based size check silently always passes. A `Range` GET
 * answers with `206` + `content-range`, whose total is the real file size, and
 * costs one byte. A server that ignores `Range` answers `200`, and its
 * `content-length` is then the whole file.
 *
 * Fails **open**: a probe that errors or times out is treated as `ok`, so a slow
 * uploadthing never costs a page its photo. An unreadable size is `ok` for the
 * same reason — unproven is not proven bad.
 */
export async function measureShareImage(
  url: string,
  now: number = Date.now()
): Promise<ShareImageProbe> {
  if (!url.startsWith('https://')) return { verdict: 'unusable', bytes: null };

  const cached = probes.get(url);
  if (cached && now - cached.at < CACHE_TTL_MS) {
    return { verdict: cached.verdict, bytes: cached.bytes };
  }

  let probe: ShareImageProbe;
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Range: 'bytes=0-0' },
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    // Never drain the body — one byte on a 206, the whole file on a 200.
    await res.body?.cancel();

    const size =
      res.status === 206
        ? totalFromContentRange(res.headers.get('content-range'))
        : Number(res.headers.get('content-length'));

    probe = {
      verdict: shareImageVerdictFromHeaders({
        ok: res.ok,
        contentType: res.headers.get('content-type') ?? '',
        size,
      }),
      bytes: Number.isFinite(size) ? size : null,
    };
  } catch {
    // Unreachable within the budget — keep the image rather than downgrade.
    // Deliberately uncached: the next attempt should get a real answer.
    return { verdict: 'ok', bytes: null };
  }

  probes.set(url, { ...probe, at: now });
  return probe;
}

/** The verdict alone — what the storefront needs to choose a card. */
export const probeShareImage = async (
  url: string,
  now: number = Date.now()
): Promise<ShareImageVerdict> => (await measureShareImage(url, now)).verdict;
